import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, Inbox, PhoneCall, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "in-review", "quoted", "booked"];

const statusCls = {
  new: "bg-gold-500/15 text-gold-400 border-gold-500/40",
  "in-review": "bg-blue-500/15 text-blue-300 border-blue-500/40",
  quoted: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  booked: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("gl_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [calls, setCalls] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [search, setSearch] = useState("");

  const loadCalls = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/calls`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setCalls(data);
    } catch {
      setCalls([]);
    }
  }, []);

  const load = useCallback(async (t) => {
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setEnquiries(data);
    } catch {
      sessionStorage.removeItem("gl_admin_token");
      setToken("");
      setEnquiries(null);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("gl_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Invalid passcode. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deleteEnquiry = async (id) => {
    if (!window.confirm("Delete this enquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((prev) => prev.filter((x) => x.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("gl_admin_token");
    setToken("");
    setEnquiries(null);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(
        `${API}/admin/enquiries/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnquiries((prev) => prev.map((x) => (x.id === id ? data : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-navy-900 grain relative flex items-center justify-center px-5">
        <div className="absolute inset-0 spotlight" />
        <motion.form
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onSubmit={login}
          data-testid="admin-login-form"
          className="relative w-full max-w-sm bg-navy-800/70 border border-white/10 rounded-xl p-8 backdrop-blur-md"
        >
          <div className="flex items-center gap-2.5 mb-8">
            <span className="w-8 h-8 bg-gold-500 rounded-sm grid place-items-center font-display font-black text-navy-900 text-lg">G</span>
            <span className="font-display font-extrabold tracking-[0.18em] text-ice-50">GOLD LINK HQ</span>
          </div>
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 block mb-2">
            Staff Passcode
          </label>
          <input
            data-testid="admin-login-passcode-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••••"
            className="w-full bg-navy-900/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-ice-50 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500/40 transition-all duration-300 mb-4"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-red-400 text-xs mb-4">{error}</p>
          )}
          <button
            data-testid="admin-login-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-navy-900 font-heading font-bold text-sm uppercase tracking-widest px-6 py-3.5 rounded-full hover:bg-gold-400 transition-colors duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Access Enquiries
          </button>
          <a href="/" data-testid="admin-back-home-link" className="block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-ice-200/40 hover:text-gold-400 mt-6 transition-colors">
            ← Back to site
          </a>
        </motion.form>
      </main>
    );
  }

  const isCallback = (x) => (x.message || "").toLowerCase().includes("call-back");
  const filtered = (enquiries || [])
    .filter((x) =>
      [x.name, x.company, x.email, x.service].join(" ").toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => Number(isCallback(b)) - Number(isCallback(a)));

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-navy-900 text-ice-50">
      <header className="border-b border-white/10 bg-navy-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 bg-gold-500 rounded-sm grid place-items-center font-display font-black text-navy-900 text-lg">G</span>
            <span className="font-display font-extrabold tracking-[0.18em]">GOLD LINK HQ</span>
            <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.2em] text-gold-500 border border-gold-500/40 rounded-full px-3 py-1 ml-3">
              Enquiries Portal
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={() => load(token)}
              className="p-2.5 rounded-full border border-white/10 text-ice-200/60 hover:text-gold-400 hover:border-gold-500/50 transition-all"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ice-200/60 hover:text-gold-400 border border-white/10 hover:border-gold-500/50 rounded-full px-4 py-2 transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          {[{ id: "enquiries", label: "Enquiries" }, { id: "calls", label: "Calls" }].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); if (t.id === "calls" && !calls) loadCalls(token); }}
              className={`font-mono text-[11px] uppercase tracking-[0.18em] px-5 py-2.5 rounded-full border transition-all duration-300 ${
                tab === t.id
                  ? "bg-gold-500 text-navy-900 border-gold-500 font-bold"
                  : "border-white/10 text-ice-200/60 hover:border-gold-500/50 hover:text-gold-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "enquiries" && (<>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl">
            Quote Enquiries{" "}
            <span className="text-gold-400">{enquiries ? `(${enquiries.length})` : ""}</span>
          </h1>
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, sector..."
            className="bg-navy-800/70 border border-white/10 rounded-full px-5 py-2.5 text-sm text-ice-50 placeholder:text-ice-200/30 focus:outline-none focus:border-gold-500 w-full sm:w-80 transition-all"
          />
        </div>

        {enquiries === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid="admin-empty-state" className="text-center py-24 border border-dashed border-white/10 rounded-xl">
            <Inbox className="w-10 h-10 text-ice-200/30 mx-auto mb-4" />
            <p className="text-ice-200/50 text-sm">No enquiries yet. New quote requests will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-enquiries-list">
            {filtered.map((x) => (
              <motion.article
                key={x.id}
                data-testid={`admin-enquiry-${x.id}`}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-navy-800/50 border border-white/5 rounded-xl p-6 grid lg:grid-cols-[1.2fr_1fr_1.4fr_auto] gap-5 items-start hover:border-white/15 transition-colors"
              >
                <div>
                  <p className="font-heading font-bold text-base">
                    {x.name}
                    {isCallback(x) && (
                      <span data-testid={`callback-badge-${x.id}`} className="ml-2 font-mono text-[9px] uppercase tracking-[0.15em] bg-gold-500 text-navy-900 font-bold rounded-full px-2.5 py-1 align-middle">
                        Call-back
                      </span>
                    )}
                  </p>
                  {x.company && <p className="text-xs text-ice-200/50 mt-0.5">{x.company}</p>}
                  <p className="font-mono text-[10px] text-ice-200/40 mt-2">
                    {new Date(x.created_at).toLocaleString("en-GB")}
                  </p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold-500/80 mb-1">{x.service}</p>
                  <p className="text-xs text-ice-200/60">{x.frequency} · {Number(x.sqft || 0).toLocaleString()} sq ft</p>
                  <p className="text-xs text-ice-200/60 mt-1">{x.email}{x.phone ? ` · ${x.phone}` : ""}</p>
                </div>
                <p className="text-sm text-ice-200/70 leading-relaxed">
                  {x.message || <span className="text-ice-200/30 italic">No additional notes.</span>}
                </p>
                <div className="flex items-center gap-2">
                <select
                  data-testid={`admin-status-select-${x.id}`}
                  value={x.status}
                  onChange={(e) => setStatus(x.id, e.target.value)}
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-2 bg-transparent cursor-pointer focus:outline-none ${statusCls[x.status] || statusCls.new}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s} className="bg-navy-900 text-ice-50">{s}</option>
                  ))}
                </select>
                <button
                  data-testid={`admin-delete-${x.id}`}
                  onClick={() => deleteEnquiry(x.id)}
                  className="p-2.5 rounded-full border border-white/10 text-ice-200/40 hover:text-red-400 hover:border-red-400/50 transition-all shrink-0"
                  aria-label="Delete enquiry"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
        </>)}

        {tab === "calls" && (
          calls === null ? (
            <div className="flex justify-center py-24">
              <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
            </div>
          ) : calls.length === 0 ? (
            <div data-testid="admin-calls-empty" className="text-center py-24 border border-dashed border-white/10 rounded-xl">
              <PhoneCall className="w-10 h-10 text-ice-200/30 mx-auto mb-4" />
              <p className="text-ice-200/50 text-sm">No calls yet. Once Goldie answers the phone, transcripts appear here.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="admin-calls-list">
              {calls.map((c) => (
                <article key={c._id} data-testid={`admin-call-${c._id}`} className="bg-navy-800/50 border border-white/5 rounded-xl p-6">
                  <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                      <p className="font-heading font-bold text-base">{c.caller || "Unknown caller"}</p>
                      <p className="font-mono text-[10px] text-ice-200/40 mt-1">
                        {c.started_at ? new Date(c.started_at).toLocaleString("en-GB") : ""} · {(c.turns || []).filter((t) => t.role === "user").length} caller turns
                      </p>
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-gold-400 border border-gold-500/40 rounded-full px-3 py-1.5">
                      Answered by Goldie
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(c.turns || []).map((t, i) => (
                      <p key={i} className="text-sm leading-relaxed">
                        <span className={`font-mono text-[10px] uppercase tracking-[0.15em] mr-2 ${t.role === "user" ? "text-ice-200/50" : "text-gold-500"}`}>
                          {t.role === "user" ? "Caller" : "Goldie"}
                        </span>
                        <span className="text-ice-200/75">{t.content}</span>
                      </p>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          )
        )}
      </div>
    </main>
  );
}
